import { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

const POPOVER_WIDTH = 240;
const VIEWPORT_MARGIN = 12;
const GAP = 6;
// Altura "ideal" do popover (rodas + botão). Vira referência p/ decidir flip.
const POPOVER_HEIGHT = 250;
// Piso de altura útil quando o espaço é apertado (rodas continuam roláveis).
const POPOVER_MIN_HEIGHT = 168;

interface TimePickerProps {
  value: string | null;
  onChange: (v: string) => void;
  className?: string;
  placeholder?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

export function TimePicker({
  value,
  onChange,
  className,
  placeholder = '--:--',
}: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const parts = (value || '22:00').split(':');
  const h = parts[0] ?? '22';
  const m = parts[1] ?? '00';
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hRef = useRef<HTMLDivElement>(null);
  const mRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Calcula posição clamped à viewport — usando Portal escapa de transforms ancestrais.
  // Vira pra cima quando não cabe embaixo e limita a altura ao espaço disponível.
  useEffect(() => {
    if (!open || !rootRef.current) return;
    function calc() {
      if (!rootRef.current) return;
      const rect = rootRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      // visualViewport reflete o teclado aberto no iOS; innerHeight não.
      const vh = window.visualViewport?.height ?? window.innerHeight;
      // Acompanha a largura do campo, limitada ao POPOVER_WIDTH.
      const width = Math.min(rect.width, POPOVER_WIDTH);
      let left = rect.left;
      if (left + width > vw - VIEWPORT_MARGIN) {
        left = vw - width - VIEWPORT_MARGIN;
      }
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

      const spaceBelow = vh - rect.bottom - GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN;
      // Abre embaixo por padrão; sobe quando não cabe e há mais espaço acima.
      const placeAbove = spaceBelow < POPOVER_HEIGHT && spaceAbove > spaceBelow;
      const avail = placeAbove ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(POPOVER_MIN_HEIGHT, Math.min(POPOVER_HEIGHT, avail));
      const top = placeAbove
        ? Math.max(VIEWPORT_MARGIN, rect.top - GAP - maxHeight)
        : rect.bottom + GAP;
      setPos({ top, left, width, maxHeight });
    }
    calc();
    window.addEventListener('resize', calc);
    window.addEventListener('scroll', calc, true);
    window.visualViewport?.addEventListener('resize', calc);
    return () => {
      window.removeEventListener('resize', calc);
      window.removeEventListener('scroll', calc, true);
      window.visualViewport?.removeEventListener('resize', calc);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      const centerItem = (container: HTMLDivElement | null, selector: string) => {
        if (!container) return;
        const item = container.querySelector<HTMLElement>(selector);
        if (!item) return;
        // Scroll só dentro do próprio wheel — sem propagar pra modal/body.
        container.scrollTop = item.offsetTop - container.clientHeight / 2 + item.offsetHeight / 2;
      };
      centerItem(hRef.current, `[data-v="${h}"]`);
      centerItem(mRef.current, `[data-v="${m}"]`);
    });
  }, [open, h, m]);

  function setH(nh: string) {
    onChange(`${nh}:${m}`);
  }
  function setM(nm: string) {
    onChange(`${h}:${nm}`);
  }

  return (
    <div ref={rootRef} className={cn('relative w-full', className)}>
      {/* Trigger — mono uppercase, borda escura quando aberto */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          'w-full inline-flex items-center justify-between rounded-lg border bg-card px-3.5 py-3 font-mono text-xs font-semibold uppercase tracking-wider transition-colors',
          open ? 'border-foreground' : 'border-border',
          value ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <span>{value || placeholder}</span>
        <Clock className="h-[15px] w-[15px] text-muted-foreground" />
      </button>

      {open && pos && typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-[70] flex flex-col rounded-xl border border-border bg-card p-3 shadow-lift"
            style={{
              top: pos.top,
              left: pos.left,
              width: pos.width,
              maxHeight: pos.maxHeight,
              animation: 'u-rise 0.2s var(--ease-underline)',
            }}
          >
            <div className="flex min-h-0 flex-1 items-stretch justify-center gap-2">
              <Wheel ref={hRef} options={HOURS} value={h} onChange={setH} label="Hora" />
              <div className="inline-flex items-center justify-center pb-1 font-mono text-[22px] font-bold text-foreground">
                :
              </div>
              <Wheel ref={mRef} options={MINUTES} value={m} onChange={setM} label="Min" />
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-2.5 w-full shrink-0 rounded-md bg-primary py-2.5 font-mono text-[11px] font-bold uppercase tracking-wider text-primary-foreground shadow-red transition active:scale-[0.97]"
            >
              Confirmar
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}

interface WheelProps {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  label: string;
}
const Wheel = forwardRef<HTMLDivElement, WheelProps>(
  ({ options, value, onChange, label }, ref) => {
    return (
      <div className="flex min-h-0 flex-1 flex-col min-w-0">
        <div className="mb-1.5 shrink-0 text-center font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div
          ref={ref}
          className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border bg-surface-2"
          style={{ scrollbarWidth: 'thin' }}
        >
          {options.map((opt) => {
            const active = opt === value;
            return (
              <button
                key={opt}
                type="button"
                data-v={opt}
                onClick={() => onChange(opt)}
                className={cn(
                  'w-full py-2.5 font-mono text-base font-bold tabular-nums transition-colors',
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted',
                )}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  },
);
Wheel.displayName = 'Wheel';
